import React from "react";
import { motion } from "framer-motion";
import { FileText } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-white font-['Inter']" data-testid="terms-page">
      <Header />

      <section className="relative pt-32 pb-16 bg-gradient-to-br from-[#0f172a] via-[#1e3a5f] to-[#0f172a] overflow-hidden">
        <div className="absolute top-20 left-10 w-72 h-72 bg-blue-600/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600/20 rounded-full blur-3xl"></div>
        
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <motion.div initial="hidden" animate="visible" variants={fadeUp}>
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500/30 to-purple-500/30 rounded-full flex items-center justify-center mx-auto mb-6 backdrop-blur-sm border border-white/10">
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-4">
              Terms of <span className="gradient-text">Service</span>
            </h1>
            <p className="text-gray-300">Last updated: April 28, 2026</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 prose prose-gray max-w-none">
          <div className="space-y-8">
            <div>
              <p className="text-gray-600">
                These Terms &amp; Conditions (&ldquo;Terms&rdquo;) form a legally binding agreement between you and{" "}
                <b>Digital Master Media LLC</b> (&ldquo;Company&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;), a Wyoming
                limited liability company with its registered office at 30 N Gould Street, Suite R, Sheridan, WY 82801, USA.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-600">
                By accessing or using our platform, you agree to these Terms &amp; Conditions and our{" "}
                <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>.
                If you do not agree, you must not use our services.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Services</h2>
              <p className="text-gray-600">
                Digital Master Media LLC operates as a pay-per-call and lead-generation platform connecting publishers
                with advertisers and buyers, including KYC, fraud-prevention and payment-processing services for approved
                publishers.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Publisher Responsibilities</h2>
              <p className="text-gray-600 mb-2">Publishers must:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Provide genuine, high-quality inbound calls</li>
                <li>Comply with all applicable laws and regulations including the U.S. Telephone Consumer Protection Act (TCPA, 47 U.S.C. § 227), CAN-SPAM Act, FTC Act, state DNC statutes, and equivalent international laws (GDPR, India DPDP Act)</li>
                <li>Obtain prior express written consent from every consumer before any call or message</li>
                <li>Avoid misleading, deceptive, or fraudulent practices</li>
              </ul>
              <p className="text-gray-600 mt-4 mb-2">Strictly prohibited:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Fake or bot-generated calls</li>
                <li>Incentivised or forced traffic</li>
                <li>Spam, robocalls or unsolicited communications</li>
                <li>Body-massage / spa calls</li>
                <li>Non-English calls</li>
                <li>Use of girls&rsquo; images in promotional posts</li>
                <li>Item / parts-selling calls</li>
              </ul>
              <p className="text-gray-600 mt-3">
                Any violation may result in <b>immediate suspension and forfeiture of all pending earnings</b>,
                under the tiered Compliance Rules of the Master Publisher Agreement (Zero Tolerance and First Warning tiers).
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Call Qualification &amp; Payments</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Payments are made only for <b>qualified calls</b> approved by the Company and/or its advertisers</li>
                <li>Call qualification is determined based on the buyer&apos;s requirements and the 150-second post-IVR live-talk buffer</li>
                <li>Invalid, duplicate, or fraudulent calls will not be paid</li>
                <li><b>Minimum payout threshold:</b> US&nbsp;$50 &mdash; earnings below this roll over to the next cycle</li>
                <li><b>Payment cycle:</b> Monthly, Net 10 &mdash; on or before the 10th business day of the following month</li>
                <li><b>Methods:</b> Payoneer, Wise, or ACH bank transfer (issued from our USA checking account)</li>
                <li>Publisher bears all bank, wire, FX and intermediary fees</li>
              </ul>
              <p className="text-gray-600 mt-3 mb-2">We reserve the right to:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Withhold payments during investigation of suspected invalid, fraudulent or non-compliant activity</li>
                <li>Reverse, deduct or claw back payments for invalid traffic, charged-back calls, or advertiser disputes</li>
                <li>Adjust payouts based on advertiser feedback or quality audits</li>
              </ul>
              <p className="text-gray-600 mt-3 italic">
                All Company decisions on payment validity and qualified-call criteria shall be final, subject only to the
                Dispute Resolution procedure in Section&nbsp;13.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Traffic &amp; Compliance</h2>
              <p className="text-gray-600 mb-2">Publishers must ensure all traffic sources:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Are lawful in every jurisdiction targeted</li>
                <li>Do not infringe trademarks, copyright, or brand rights of any third party</li>
                <li>Do not misrepresent services, offers, or affiliations</li>
                <li>Maintain verifiable proof of consumer consent for at least four (4) years</li>
              </ul>
              <p className="text-gray-600 mt-3">
                Non-compliance may lead to <b>termination without notice</b> and forfeiture of pending payouts.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Account Verification (KYC)</h2>
              <p className="text-gray-600">
                We reserve the right to request identity verification at any time. To run traffic on our network you must
                submit a valid government-issued photo ID, which is processed in line with our{" "}
                <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>.
                Uploaded IDs are watermarked &ldquo;FOR VERIFICATION PURPOSE ONLY&rdquo; before storage and embedded as
                Exhibit&nbsp;A in the Master Publisher Agreement. The Company will delete the uploaded ID image within
                seven (7) days after you leave the network or your account is terminated. Failure to comply with KYC
                requests may result in account suspension or termination and forfeiture of pending payouts.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. Intellectual Property &amp; Confidentiality</h2>
              <p className="text-gray-600">
                The website, software, dashboards, brand marks (&ldquo;Digital Master Media&rdquo;), graphics and content are
                owned by or licensed to the Company and protected by U.S. and international copyright, trademark and other IP laws.
                We grant you a limited, revocable, non-exclusive, non-transferable license to access the services for the purposes
                contemplated by these Terms. All other rights are reserved. Information you receive through our platform &mdash;
                including offer details, payout amounts, conversion data and advertiser identities &mdash; is confidential and may
                not be disclosed to any third party without our prior written consent.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. Limitation of Liability</h2>
              <p className="text-gray-600 uppercase text-sm tracking-wide">
                To the maximum extent permitted by law, Digital Master Media LLC shall not be liable for: (a) any loss of
                profits, revenue, data, goodwill or other intangible losses; (b) buyer rejection of calls or leads;
                (c) technical issues, downtime, or service interruptions; or (d) any indirect, incidental, special, consequential,
                exemplary or punitive damages. Our aggregate liability for any claim arising out of or relating to these Terms
                shall not exceed the greater of (i) total payouts actually paid to you in the six (6) months preceding the
                claim, or (ii) US&nbsp;$100.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Indemnification</h2>
              <p className="text-gray-600">
                You agree to indemnify, defend and hold harmless the Company and its officers, directors, employees and agents
                from and against any claims, losses, damages, liabilities, judgments and expenses (including reasonable attorneys&apos;
                fees) arising out of: (a) your breach of these Terms or the Master Publisher Agreement; (b) your violation of any
                law or third-party right; (c) any traffic or content submitted by you; or (d) your fraud, willful misconduct or
                gross negligence.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Termination</h2>
              <p className="text-gray-600 mb-2">We may suspend or terminate accounts at our sole discretion for:</p>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li>Fraudulent activity</li>
                <li>Violation of these Terms or the Master Publisher Agreement</li>
                <li>Suspicious or harmful behaviour</li>
                <li>Request by law enforcement or any competent authority</li>
                <li>Discontinuation of the service</li>
              </ul>
              <p className="text-gray-600 mt-3">
                No payments will be made for invalid or non-compliant traffic. Sections relating to payment claw-back, IP,
                confidentiality, limitation of liability, indemnification, governing law and dispute resolution survive termination.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Electronic Signatures</h2>
              <p className="text-gray-600">
                You consent to receive communications from us electronically. Electronic acceptance of these Terms or the
                Master Publisher Agreement (including via the signup form, ticking a consent box, or clicking &ldquo;I Agree&rdquo;)
                constitutes a valid and binding signature under the U.S. Electronic Signatures in Global and National Commerce
                Act (E-SIGN Act, 15 U.S.C. § 7001), the Uniform Electronic Transactions Act (UETA), and equivalent international laws.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Governing Law</h2>
              <p className="text-gray-600">
                These Terms are governed by and construed in accordance with the laws of the <b>State of Wyoming, United States of America</b>,
                without regard to its conflict-of-laws rules. Each party irrevocably submits to the exclusive jurisdiction of the state
                and federal courts located in Sheridan County, Wyoming for the resolution of any dispute. Where the publisher resides
                outside the United States, the publisher additionally agrees that compliance with applicable local laws of their
                jurisdiction is their sole responsibility.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Dispute Resolution</h2>
              <ul className="list-disc pl-6 text-gray-600 space-y-2">
                <li><b>Step 1 &mdash; Good faith.</b> Parties shall first attempt to resolve any dispute by written notice and good-faith discussion for thirty (30) days.</li>
                <li><b>Step 2 &mdash; Binding arbitration.</b> Disputes not resolved in Step 1 shall be finally settled by binding arbitration administered in Sheridan, Wyoming, USA, with one (1) arbitrator under applicable commercial arbitration rules. Judgment on the award may be entered in any court of competent jurisdiction.</li>
                <li><b>Step 3 &mdash; Equitable relief.</b> Either party may seek injunctive or other equitable relief from any competent court to protect intellectual property or confidential information.</li>
                <li><b>Class-action waiver.</b> To the maximum extent permitted by law, you waive any right to participate in a class, collective or representative action against the Company.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Changes to Terms</h2>
              <p className="text-gray-600">
                We may update these Terms at any time. Material changes will be notified by email and / or posted on this page
                with a new &ldquo;Last updated&rdquo; date. Continued use of our services indicates acceptance of the updated Terms.
              </p>
            </div>

            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Contact</h2>
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-6">
                <p className="text-gray-900 font-bold mb-2">Digital Master Media LLC</p>
                <p className="text-gray-600">30 N Gould Street, Suite R</p>
                <p className="text-gray-600">Sheridan, WY 82801, USA</p>
                <p className="text-gray-600">Email: abidali@digitalmastermedia.com</p>
                <p className="text-gray-600">Phone: +1 (855) 471-2511</p>
                <p className="text-gray-600">Website: digitalmastermedia.com</p>
              </div>
              <p className="text-gray-500 text-sm mt-4">
                These Terms together with our{" "}
                <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>{" "}
                and (for approved publishers) the Master Publisher Agreement constitute the entire agreement between you and the Company.
              </p>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TermsPage;
